import { 
    collection, 
    getDocs, 
    doc, 
    getDoc, 
    addDoc, 
    Timestamp,
    updateDoc,
} from "firebase/firestore";

import { firestore } from "../firebase/firebaseConfig";

/**
 * Fetch all subscription plans from the Firestore `plans` collection.
 * @returns {Promise<Array>} Array of plan objects.
 */
export const getAllPlans = async () => {
  try {
    const plansRef = collection(firestore, "plans");
    const querySnapshot = await getDocs(plansRef);

    if (querySnapshot.empty) {
      console.warn("[getAllPlans] No plans found.");
      return [];
    }

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[getAllPlans] Error fetching plans:", error);
    throw new Error("Failed to fetch plans.");
  }
};

/**
 * Fetch a specific subscription plan by ID.
 * @param {string} planId - The unique ID of the plan.
 * @returns {Promise<Object|null>} The plan object or null if not found.
 */
export const getPlanById = async (planId) => {
  try {
    const planRef = doc(firestore, "plans", planId);
    const planSnap = await getDoc(planRef);

    if (!planSnap.exists()) {
      console.warn(`[getPlanById] Plan not found for ID: ${planId}`);
      return null;
    }

    console.log('planSnap content:', planSnap);

    return { id: planSnap.id, ...planSnap.data() };
  } catch (error) {
    console.error("[getPlanById] Error fetching plan by ID:", error);
    throw new Error("Failed to fetch the plan.");
  }
};

/**
 * Add a new subscription plan to the `plans` collection.
 * @param {Object} planDetails - The details of the plan to add.
 * @returns {Promise<Object>} The created plan object with its ID.
 */
export const createPlan = async (planDetails) => {
  try {
    const plansRef = collection(firestore, "plans");
    const newPlan = { ...planDetails, createdAt: Timestamp.now() };
    const planDocRef = await addDoc(plansRef, newPlan);

    return { id: planDocRef.id, ...newPlan };
  } catch (error) {
    console.error("[createPlan] Error creating plan:", error);
    throw new Error("Failed to create the plan.");
  }
};

/**
 * Update an existing subscription plan in the `plans` collection.
 * @param {string} planId - The unique ID of the plan.
 * @param {Object} updatedData - The fields to update.
 * @returns {Promise<void>}
 */
export const updatePlan = async (planId, updatedData) => {
  try {
    const planRef = doc(firestore, "plans", planId);
    await updateDoc(planRef, updatedData);
    console.log(`[updatePlan] Plan ${planId} updated successfully.`);
  } catch (error) {
    console.error(`[updatePlan] Error updating plan ${planId}:`, error);
    throw new Error("Failed to update the plan.");
  }
};

/**
 * Fetches a plan from the `plans` collection using the `stripePriceId` in the `billingCycles` array.
 * @param {string} stripePriceId - The Stripe Price ID to search for.
 * @returns {Promise<Object|null>} The plan details or null if not found.
 */
export const fetchPlanByStripePriceId = async (stripePriceId) => {
    try {
      // Reference the `plans` collection
      const plansRef = collection(firestore, "plans");
  
      // Retrieve all plans
      const querySnapshot = await getDocs(plansRef);
  
      if (querySnapshot.empty) {
        console.warn(`[fetchPlanByStripePriceId] No plans found in the collection.`);
        return null;
      }
  
      // Iterate through plans to find the matching stripePriceId in billingCycles
      for (const doc of querySnapshot.docs) {
        const planData = doc.data();
  
        // Check if the `billingCycles` array contains the target stripePriceId
        const matchingCycle = planData.billingCycles.find(
          (cycle) => cycle.stripePriceId === stripePriceId
        );
  
        if (matchingCycle) {
          console.log("[fetchPlanByStripePriceId] Plan found:", {
            id: doc.id,
            ...planData,
            matchingCycle, // Include the specific cycle details
          });
          return {
            id: doc.id,
            ...planData,
            matchingCycle, // Return the specific cycle that matches
          };
        }
      }
  
      console.warn(`[fetchPlanByStripePriceId] No plan found for Stripe Price ID: ${stripePriceId}`);
      return null;
    } catch (error) {
      console.error("[fetchPlanByStripePriceId] Error fetching plan:", error);
      throw new Error("Failed to fetch plan by Stripe Price ID. Please try again.");
    }
  };